import { useState, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Clinical from "@/Clinical";
import Couples from "@/Couples";
import Cultural from "@/Cultural";
import About from "@/About";
import Concern, { CONCERNS, BENEFITS, DEFAULT_BENEFITS } from "@/Concern";
import Fees from "@/Fees";
import ApproachDetail from "@/ApproachDetail";
import Resources from "@/Resources";
import CrisisSupport from "@/CrisisSupport";
import ContactPage from "@/Contact";
import SiteHeader from "@/components/SiteHeader";
import {
  ArrowRight, ArrowUpRight, User, Shapes, Sprout, Home, Heart,
  Flower2, Baby, HeartCrack, ShieldAlert, Users, Moon, Mail, Phone, MapPin, Instagram, Globe,
  MessageCircle, LifeBuoy, CloudRain, Wind, HeartHandshake, Zap, Leaf, Flower, Sparkles, BookOpen, Waves,
} from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => <p className="eyebrow">{children}</p>;

const TopBar = () => (
  <div className="bg-[#eae2d6] border-b border-[#2c1f20]/10 text-xs text-[#2c1f20]/65">
    <div className="px-6 md:px-14 py-2 flex flex-wrap justify-between gap-2">
      <span>Virtual across Ontario · In-person in Milton &amp; Burlington</span>
      <span className="flex flex-wrap gap-6">
        <a href="tel:+13656172135" className="flex items-center gap-1.5 hover:text-[#6e4550] transition-colors"><Phone size={12} /> (365) 617-2135</a>
        <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-1.5 hover:text-[#6e4550] transition-colors"><Mail size={12} /> momna@hayahealingpsychotherapy.com</a>
      </span>
    </div>
  </div>
);

const TRUST = [
  ["CRPO", "Registered Psychotherapist (Qualifying) · College of Registered Psychotherapists of Ontario"],
  ["EN / UR", "Sessions in English & Urdu"],
  ["2", "Clinics — Milton & Burlington"],
  ["ON", "Virtual, Ontario-wide"],
];

const TrustStrip = () => (
  <section data-testid="trust-strip" className="border-y border-[#2c1f20]/10 bg-[#eae2d6]/70">
    <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#2c1f20]/10">
      {TRUST.map(([n, l]) => (
        <div key={l} className="py-6 px-4 text-center">
          <p className="font-serif-display text-2xl text-[#6e4550]">{n}</p>
          <p className="text-xs text-[#2c1f20]/55 mt-1">{l}</p>
        </div>
      ))}
    </div>
  </section>
);

const Hero = () => (
  <section data-testid="hero-section" id="about" className="relative px-6 md:px-14 pt-20 md:pt-32 pb-24 md:pb-36">
    <div className="md:grid md:grid-cols-12 md:gap-10 items-end">
      <div className="md:col-span-7 relative z-10">
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="flex flex-wrap items-center gap-3">
          <Eyebrow>Psychotherapy Practice · Milton, Burlington &amp; Online Across Ontario</Eyebrow>
          <span className="text-[11px] tracking-[0.15em] uppercase border border-[#2c1f20]/20 rounded-full px-3 py-1 text-[#2c1f20]/70">
            ● Virtual sessions · Ontario
          </span>
          <span className="text-[11px] tracking-[0.15em] uppercase border border-[#2c1f20]/20 rounded-full px-3 py-1 text-[#2c1f20]/70">
            In-person · Milton &amp; Burlington
          </span>
        </motion.div>
        <h1 data-testid="hero-heading" className="font-serif-display mt-8 text-[clamp(2.1rem,4.6vw,3.6rem)] leading-[1.1] font-normal max-w-3xl">
          {["Evidence-based psychotherapy", "for individuals,", "couples, and families."].map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span className="block"
                initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.15 + i * 0.13, ease }}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-6 font-serif-display italic text-base text-[#6e4550]">
          Individualized care. Meaningful change.
        </motion.p>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-5 text-sm font-semibold tracking-[0.18em] uppercase text-[#2c1f20]/70">
          Clinical Psychotherapy for Adults, Couples, Families &amp; Youth
        </motion.p>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={5} className="mt-6 max-w-xl text-[#2c1f20]/75 leading-relaxed">
          Haya Healing Psychotherapy provides evidence-informed psychotherapy for anxiety, trauma, stress,
          relationship difficulties, emotional regulation, identity concerns, and life transitions. Treatment is
          individualized according to your presenting concerns, therapeutic goals, strengths, and circumstances.
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={6} className="mt-8 flex flex-wrap gap-6">
          <a data-testid="hero-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book a Consultation <ArrowRight size={15} /></a>
          <a data-testid="hero-explore-link" href="#services" className="pill-btn pill-outline">Explore Services</a>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5, ease }}
        className="relative mt-16 md:mt-0 md:col-span-5">
        <div className="rounded-md overflow-hidden shadow-[0_30px_80px_-40px_rgba(44,31,32,0.35)]">
          <img src="https://images.pexels.com/photos/6615806/pexels-photo-6615806.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Calm psychotherapy consultation room with a beige armchair and minimalist warm decor" data-testid="hero-artwork" className="w-full h-[400px] md:h-[520px] object-cover" />
        </div>
      </motion.div>
    </div>
  </section>
);

const Meaning = () => (
  <section className="px-6 md:px-14 py-28 md:py-40">
    <div className="grid md:grid-cols-12 gap-16 items-center">
      <div className="md:col-span-6">
        <Reveal><Eyebrow>01 · The meaning of Haya</Eyebrow></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4">A practice built on <em className="text-[#6e4550]">dignity, safety, and self-respect.</em></h2></Reveal>
        <Reveal i={2}>
          <p className="mt-6 text-[#2c1f20]/75 leading-relaxed">
            The word Haya is often translated as modesty, but its meaning extends far beyond appearance. At its core,
            Haya reflects an inner sense of awareness, dignity, and the quiet voice that guides us toward safety,
            trust, and self-respect.
          </p>
        </Reveal>
        <Reveal i={3}>
          <p className="mt-6 text-[#2c1f20]/75 leading-relaxed">
            Haya Healing Psychotherapy is grounded in this philosophy: a space where inner safety and trust allow
            meaningful healing to unfold.
          </p>
        </Reveal>
      </div>
      <Reveal className="md:col-span-5 md:col-start-8">
        <div className="rounded-md overflow-hidden">
          <img src="https://images.unsplash.com/photo-1691036561573-4b76998b60de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxlZGl0b3JpYWwlMjBhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3IlMjBiZWlnZXxlbnwwfHx8fDE3OTAyNjYyNjl8MA&ixlib=rb-4.1.0&q=85"
            alt="Neutral interior with a subtle art piece" data-testid="meaning-artwork" className="w-full h-[380px] md:h-[460px] object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const MODALITIES = [
  ["Cognitive Behavioural Therapy (CBT)", "Structured work on the thoughts and behaviours driving anxiety, depression, and everyday stress.", "/approach/cbt"],
  ["Cognitive Processing Therapy (CPT)", "A leading evidence-based treatment for PTSD and trauma, helping you reprocess beliefs left behind by difficult experiences.", "/approach/cpt"],
  ["Dialectical Behaviour Therapy (DBT)", "Practical skills for emotional regulation, distress tolerance, and navigating intense relationship conflict.", "/approach/dbt"],
  ["Acceptance & Commitment Therapy (ACT)", "Build psychological flexibility — making room for difficult thoughts while moving toward what matters to you.", "/approach/act"],
  ["Gottman Method Couples Therapy", "One of the most researched approaches to couples therapy — communication, conflict repair, and rebuilding trust.", "/approach/gottman"],
  ["Humanistic Therapy", "Client-centred work that strengthens self-understanding, self-worth, and authentic growth.", "/approach/humanistic"],
  ["Psychodynamic Therapy", "Explore how early experiences and unconscious patterns shape your current relationships and choices.", "/approach/psychodynamic"],
  ["Trauma-Informed Care", "Safety-first treatment that recognizes how trauma affects the mind, body, and behaviour.", "/approach/trauma-informed"],
  ["Relational Therapy", "Healing through the therapeutic relationship itself, with attention to attachment patterns.", "/approach/relational"],
  ["Culturally Responsive Therapy", "Care that treats culture, faith, and family systems as part of the clinical picture — not separate from it.", "/cultural"],
  ["Play Therapy", "Developmentally tailored therapy that helps children express and process big feelings through play.", "/approach/play-therapy"],
];

const Approach = () => (
  <section id="approach" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10">
    <Reveal><Eyebrow>02 · How we work</Eyebrow></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl">Our clinical approach to <em className="text-[#6e4550]">psychotherapy.</em></h2></Reveal>
    <Reveal i={2}>
      <p className="mt-6 max-w-2xl text-xs font-semibold tracking-[0.2em] uppercase text-[#2c1f20]/55 leading-relaxed">
        Individualized psychotherapy grounded in evidence-based practice and informed by the complexity of each client's experiences.
      </p>
    </Reveal>
    <Reveal i={3}>
      <p className="mt-6 max-w-2xl text-[#2c1f20]/75 leading-relaxed">
        We recognize that each client brings a unique set of experiences, strengths, and concerns to therapy.
        Treatment is thoughtfully tailored to your needs and goals while considering the personal, relational, and
        cultural context of your experiences.
      </p>
    </Reveal>
    <Reveal i={4} className="mt-10">
      <p className="eyebrow mb-4">Approaches we draw from — select any to learn more</p>
      <div className="flex flex-wrap gap-2 max-w-4xl" data-testid="approach-list">
        {MODALITIES.map(([name, desc, href], i) => (
          <a key={name} href={href} data-testid={`approach-item-${i + 1}`}
            className="chip hover:bg-[#6e4550] hover:text-[#fcfcf9] transition-colors">
            {name}
          </a>
        ))}
      </div>
    </Reveal>
  </section>
);


const TherapistTeaser = () => (
  <section data-testid="therapist-teaser" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10">
    <div className="max-w-2xl">
      <Reveal><Eyebrow>04 · Meet Momna</Eyebrow></Reveal>
      <Reveal i={1}>
        <div className="flex items-center gap-5 mt-3">
          <img src="/momna-headshot-new.jpg" alt="Momna Sajid, Registered Psychotherapist (Qualifying)" data-testid="teaser-headshot"
            className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover object-top border border-[#2c1f20]/10 shadow-[0_10px_30px_-12px_rgba(44,31,32,0.25)]" />
          <h2 className="font-serif-display text-3xl md:text-4xl">Meet <em className="text-[#6e4550]">Momna.</em></h2>
        </div>
      </Reveal>
      <Reveal i={2}>
        <div className="mt-5 space-y-4 text-[#2c1f20]/75 leading-relaxed">
          <p>
            Momna Sajid is a Registered Psychotherapist (Qualifying) providing individual, couples, and family
            psychotherapy across Ontario.
          </p>
          <p>
            Her approach is evidence-informed, trauma-informed, culturally responsive, and collaborative. Treatment
            draws from cognitive-behavioural, trauma-focused, relational, humanistic, and acceptance-based
            approaches and is individualized according to each client's presenting concerns and therapeutic goals.
          </p>
        </div>
      </Reveal>
      <Reveal i={3} className="mt-7"><a data-testid="therapist-page-link" href="/about" className="pill-btn pill-solid">Learn More About Momna <ArrowRight size={15} /></a></Reveal>
    </div>
  </section>
);

const SERVICES = [
  { icon: User, title: "Individual Psychotherapy", page: "/concern/individual-therapy", desc: "One-on-one support to explore your thoughts, emotions, and patterns in a confidential, non-judgmental space." },
  { icon: Shapes, title: "Child Psychotherapy", page: "/concern/child-therapy", desc: "Play-based, developmentally tailored sessions helping children name, understand, and move through big feelings." },
  { icon: Sprout, title: "Teen Psychotherapy", page: "/concern/teen-therapy", desc: "An honest, pressure-free space for teens to navigate identity, school stress, friendships, and emotional change." },
  { icon: Home, title: "Family Therapy", page: "/concern/family-counselling", desc: "Strengthen communication, repair ruptures, and rebuild trust and connection across the family system." },
  { icon: Heart, title: "Couples & Marriage Counselling", page: "/couples", desc: "Couples psychotherapy for South Asian, Muslim, and intercultural couples — communication, trust, in-law dynamics, and navigating family expectations. Grounded in the Gottman Method, with sessions in English & Urdu.", updated: true },
  { icon: Globe, title: "Culturally Informed Therapy", page: "/cultural", desc: "Therapy that treats culture, family systems, and social context as part of the clinical picture — not separate from psychological wellbeing.", updated: true },
  { icon: Flower2, title: "Women's Mental Health", page: "/concern/womens-issues", desc: "Motherhood, burnout, identity transitions, relationship changes, family expectations, and cultural or intergenerational stress." },
  { icon: Waves, title: "Emotional Regulation", page: "/concern/emotional-regulation", desc: "Difficulty managing emotions, intense emotional responses, coping difficulties, and patterns of avoidance or reactivity." },
  { icon: Baby, title: "Pre-natal / Postpartum", page: "/concern/prenatal-postpartum", desc: "Compassionate support through pregnancy, birth, postpartum identity shifts, and the realities of early motherhood." },
  { icon: HeartCrack, title: "Divorce / Separation", page: "/concern/divorce-separation", desc: "Steady guidance through grief, co-parenting, and the slow work of rebuilding your sense of self." },
  { icon: ShieldAlert, title: "Toxic Family Dynamics", page: "/concern/toxic-family-dynamics", desc: "Address intergenerational patterns, establish protective boundaries, and rebuild emotional autonomy." },
  { icon: Users, title: "Parent Support & Coaching", page: "/concern/parent-support-coaching", desc: "Practical, compassionate coaching for parents navigating discipline, emotional attunement, and the realities of raising children today." },
  { icon: Moon, title: "Menopause & Perimenopause Psychotherapy", page: "/concern/menopause-perimenopause", desc: "Compassionate support through the emotional, cognitive, and identity shifts that come with perimenopause and menopause." },
  { icon: MessageCircle, title: "Relationships & Dating", page: "/concern/relationships-dating", desc: "Work on the patterns that shape how you date, attach, and choose partners." },
  { icon: LifeBuoy, title: "Trauma Psychotherapy", page: "/concern/trauma-therapy", desc: "Structured, trauma-informed care for the effects of difficult or overwhelming experiences." },
  { icon: CloudRain, title: "Depression", page: "/concern/depression", desc: "Evidence-based treatment for low mood, numbness, and lost motivation." },
  { icon: Wind, title: "Anxiety", page: "/concern/anxiety", desc: "Structured, practical tools for worry, panic, and overthinking." },
  { icon: HeartHandshake, title: "Premarital Counselling", page: "/concern/premarital-counselling", desc: "Prepare for marriage with structured work on communication, family expectations, and shared values." },
  { icon: Zap, title: "ADHD", page: "/concern/adhd", desc: "Practical, shame-free support for focus, follow-through, and overwhelm." },
  { icon: Leaf, title: "Grief Counselling", page: "/concern/grief-counselling", desc: "Steady support through loss — with no fixed timeline." },
  { icon: Flower, title: "South Asian Women's Psychotherapy", page: "/concern/south-asian-womens-therapy", desc: "Care for the weight of cultural expectation — no explaining required. In English & Urdu." },
  { icon: Sparkles, title: "South Asian Couples Therapy", page: "/couples#south-asian-couples", desc: "Dedicated couples work on in-law dynamics, intergenerational expectations, and family pressures. In English & Urdu.", updated: true },
  { icon: BookOpen, title: "Faith-Based Psychotherapy", page: "/concern/faith-based-therapy", desc: "Evidence-based therapy that integrates your faith into the clinical work." },
];

const COUPLES_FOCUS = {
  title: "Couples & Marriage Counselling",
  intro: [
    "Structured couples therapy grounded in the Gottman Method — one of the most researched approaches to relationship work.",
    "Sessions address communication, recurring conflict, trust, and the family and cultural contexts around the relationship — including dedicated South Asian couples therapy in English and Urdu.",
  ],
  bullets: ["Communication difficulties", "Recurring conflict", "Trust and betrayal", "In-law & extended family dynamics", "Cultural and religious differences", "Intimacy concerns"],
  benefits: [
    ["Rebuild Communication", "Learn to raise hard topics and actually be heard — without conversations turning into conflict."],
    ["Repair Trust & Conflict", "Work through betrayals and recurring arguments with a structured, researched framework."],
    ["Navigate Family & Culture Together", "Address in-law involvement, intergenerational expectations, and community pressures as a team."],
  ],
  link: "/couples",
  linkLabel: "Explore couples therapy",
};

const FOCUS_TABS = [
  { key: "individual-therapy", label: "Individual" },
  { key: "child-therapy", label: "Child" },
  { key: "teen-therapy", label: "Teen" },
  { key: "family-counselling", label: "Family" },
  { key: "couples", label: "Couples" },
];

const Focus = () => {
  const [active, setActive] = useState("individual-therapy");
  const isCouples = active === "couples";
  const data = isCouples
    ? COUPLES_FOCUS
    : { ...CONCERNS[active], benefits: BENEFITS[active] || DEFAULT_BENEFITS, link: `/concern/${active}`, linkLabel: `Explore ${CONCERNS[active].title.toLowerCase()}` };
  return (
    <section id="focus" data-testid="focus-section" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10 bg-[#f1ece4]/50">
      <Reveal><Eyebrow>05 · Psychotherapy services</Eyebrow></Reveal>
      <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl">Individual, child, teen, family &amp; <em className="text-[#6e4550]">couples psychotherapy.</em></h2></Reveal>
      <Reveal i={2}>
        <div className="flex flex-wrap gap-2 mt-10" role="tablist">
          {FOCUS_TABS.map((t) => (
            <button key={t.key} role="tab" aria-selected={active === t.key} data-testid={`focus-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`pill-btn text-sm transition-colors ${active === t.key ? "bg-[#2c1f20] text-[#fcfcf9]" : "pill-outline"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </Reveal>
      <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease }}
        className="mt-12 grid lg:grid-cols-12 gap-10 items-start" data-testid="focus-panel">
        <div className="lg:col-span-5">
          <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">{data.title}</h3>
          <div className="mt-5 space-y-4 text-[#2c1f20]/75 leading-relaxed">
            {data.intro.map((p) => <p key={p}>{p}</p>)}
          </div>
          <div className="mt-7 flex flex-wrap gap-4 items-center">
            <a data-testid="focus-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book Initial Consultation <ArrowRight size={15} /></a>
            <a data-testid="focus-page-link" href={data.link} className="pill-btn pill-outline">{data.linkLabel} <ArrowRight size={15} /></a>
          </div>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-6">
          <div className="bg-white/70 border border-[#2c1f20]/10 rounded-md p-6">
            <p className="eyebrow">Support may focus on</p>
            <ul className="mt-4 space-y-2.5">
              {data.bullets.map((b) => (
                <li key={b} className="flex gap-3 text-sm text-[#2c1f20]/80">
                  <span className="text-[#8a5a63] mt-0.5">✦</span><span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white/70 border border-[#2c1f20]/10 rounded-md p-6">
            <p className="eyebrow">What changes</p>
            <ul className="mt-4 space-y-4">
              {data.benefits.map(([t, d]) => (
                <li key={t}>
                  <p className="font-serif-display text-lg leading-snug">{t}</p>
                  <p className="text-xs text-[#2c1f20]/70 mt-1 leading-relaxed">{d}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const Services = () => (
  <section id="services" data-testid="services-section" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10">
    <Reveal><Eyebrow>07 · Areas of clinical focus</Eyebrow></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-3xl">Psychotherapy for a range of <em className="text-[#6e4550]">mental health concerns.</em></h2></Reveal>
    <Reveal i={2}>
      <p className="mt-4 max-w-2xl text-[#2c1f20]/75 leading-relaxed">
        Haya Healing Psychotherapy provides individualized, evidence-informed psychotherapy for a range of
        psychological, emotional, and relational concerns. Treatment is culturally responsive and tailored to each
        client's needs, goals, and circumstances.
      </p>
      <p className="mt-3 text-xs text-[#2c1f20]/55 leading-relaxed">
        Virtual sessions across Ontario · In-person at Halton Medix Walk-In Clinic in Milton and Burlington.
      </p>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
      {SERVICES.map((s, i) => (
        <Reveal key={s.title} i={0} className="h-full">
          <a href={s.page}
            data-testid={s.updated ? "couples-service-row" : `service-row-${i + 1}`}
            className="group card flex flex-col h-full">
            <div className="flex items-start justify-between">
              <span className="w-9 h-9 rounded-full bg-[#f4e6e2] flex items-center justify-center text-[#8a5a63]"><s.icon size={16} /></span>
              <span className="text-xs text-[#2c1f20]/35 font-medium">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <span className="font-serif-display text-xl md:text-2xl mt-4 leading-snug">{s.title}</span>
            <span className="block text-sm text-[#2c1f20]/65 mt-2 leading-relaxed">{s.desc}</span>
            <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#6e4550]">
              Learn more <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </a>
        </Reveal>
      ))}
    </div>
  </section>
);

const LOCATIONS = [
  {
    city: "Milton",
    desc: "Individual, couples, and family therapy in Milton — including South Asian couples therapy in English and Urdu. In-person sessions at Halton Medix Walk-In Clinic, serving Milton, Campbellville, and the surrounding Halton region.",
    address: "Halton Medix Walk-In Clinic · 100 Bronte St S, Suite 2, Milton, ON",
  },
  {
    city: "Burlington",
    desc: "In-person psychotherapy in Burlington for individuals, couples, and families — minutes from Oakville, Waterdown, and Hamilton. Sessions available in English and Urdu at Halton Medix Walk-In Clinic.",
    address: "Halton Medix Walk-In Clinic · 3061 Walkers Line, Units 4 & 5, Burlington, ON",
  },
];

const Locations = () => (
  <section id="locations" data-testid="locations-section" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10 bg-[#eae2d6]/60">
    <Reveal><Eyebrow>08 · In-person sessions</Eyebrow></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4">Therapy in <em className="text-[#6e4550]">Milton &amp; Burlington.</em></h2></Reveal>
    <Reveal i={2}>
      <p className="mt-4 max-w-2xl text-[#2c1f20]/75 leading-relaxed">
        Alongside virtual sessions across Ontario, we offer therapy in person at Halton Medix Walk-In Clinic, with locations in Milton and Burlington.
      </p>
    </Reveal>
    <div className="grid md:grid-cols-2 gap-6 mt-16">
      {LOCATIONS.map((l, i) => (
        <Reveal key={l.city} i={i + 1} className="card">
          <div className="w-10 h-10 rounded-full bg-[#f4e6e2] flex items-center justify-center text-[#8a5a63]">
            <MapPin size={18} />
          </div>
          <h3 className="font-serif-display text-3xl mt-4">{l.city}</h3>
          <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{l.desc}</p>
          <p className="text-xs text-[#2c1f20]/55 mt-4">{l.address}</p>
          <a data-testid={`book-${l.city.toLowerCase()}-link`} href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-outline mt-5 text-sm">
            Book in {l.city} <ArrowRight size={14} />
          </a>
        </Reveal>
      ))}
    </div>
  </section>
);

const MARQUEE_MSG = "In-person sessions now available in Milton & Burlington ✦ Sessions in English & Urdu ✦ Complimentary 15-minute consultation ✦ ";

const AnnouncementMarquee = () => (
  <div data-testid="announcement-marquee" className="bg-[#6e4550] text-[#fcfcf9] py-3 overflow-hidden">
    <div className="marquee-track font-serif-display text-lg whitespace-nowrap">
      <span className="pr-6">{MARQUEE_MSG.repeat(3)}</span>
      <span className="pr-6">{MARQUEE_MSG.repeat(3)}</span>
    </div>
  </div>
);

const Patterns = () => (
  <section data-testid="patterns-section" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10 bg-[#eae2d6]/60">
    <div className="max-w-3xl">
      <Reveal><Eyebrow>06 · Clinical perspective</Eyebrow></Reveal>
      <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Understanding the <em className="text-[#6e4550]">patterns behind distress.</em></h2></Reveal>
      <Reveal i={2}>
        <div className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed">
          <p>Sometimes the problem is not simply what is happening in the present.</p>
          <p>
            Our current emotional responses can be shaped by previous experiences, learned coping strategies,
            attachment patterns, family relationships, and beliefs we have developed about ourselves and others.
          </p>
          <p>
            Therapy provides an opportunity to slow these patterns down, understand their function, and develop
            alternative ways of responding.
          </p>
        </div>
      </Reveal>
      <Reveal i={3}>
        <p className="mt-8 font-serif-display italic text-base md:text-lg text-[#6e4550] leading-snug">
          Insight can create understanding. Therapy turns that understanding into change.
        </p>
      </Reveal>
    </div>
  </section>
);

const STEPS = [
  ["01", "Initial Consultation", "We begin by discussing what brings you to therapy, your current concerns, and what you hope to change. This initial conversation also provides an opportunity to determine whether the therapeutic relationship and services are an appropriate fit."],
  ["02", "Assessment & Treatment Planning", "Early sessions focus on developing an understanding of your current difficulties, relevant history, patterns of functioning, and treatment goals. Together, we identify areas of focus and establish a collaborative treatment plan."],
  ["03", "Therapeutic Intervention", "Treatment may involve developing coping strategies, examining patterns of thinking and behaviour, processing difficult experiences, strengthening emotional regulation, improving communication, or exploring underlying relational patterns."],
  ["04", "Ongoing Review", "Therapy is an ongoing collaborative process. Progress and treatment goals are reviewed throughout therapy, with interventions adjusted according to your changing needs."],
];

const Steps = () => (
  <section className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10 bg-[#f1ece4]/50">
    <Reveal><Eyebrow>03 · How therapy works</Eyebrow></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl">From first consultation to <em className="text-[#6e4550]">treatment plan.</em></h2></Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
      {STEPS.map(([n, t, d], i) => (
        <Reveal key={n} i={i + 1} className="card">
          <p className="font-serif-display text-3xl text-[#8a5a63]">{n}</p>
          <h3 className="font-serif-display text-2xl mt-3">{t}</h3>
          <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const FAQS = [
  ["What does a session actually look like?", "Sessions are available virtually across Ontario, or in person at Halton Medix Walk-In Clinic in Milton and Burlington. Virtual sessions take place over a secure, PHIPA-compliant video platform—you'll receive a private link before your appointment, simply find a quiet, comfortable space, click the link, and we'll begin. Virtual sessions are clinically equivalent to in-person therapy for most concerns and offer added flexibility and privacy."],
  ["How long are sessions and how often will we meet?", "Sessions are typically 50 minutes, with a 90-minute initial intake. Most clients begin weekly or biweekly, and we adjust the rhythm together as you grow."],
  ["Are sessions confidential?", "Yes. Everything you share is held in strict confidence, in line with the CRPO's Professional Practice Standards and Code of Ethics. In Ontario, confidentiality has specific legal limits: a risk of serious harm to you or someone else, suspected abuse or neglect of a child, disclosure required for legal proceedings (such as a subpoena), concerns of sexual abuse by a regulated health professional, or a quality-assurance review by the College. These limits are reviewed together in your first session, so you always know where you stand."],
  ["Do you bill insurance?", "We don't direct bill insurance, but you'll receive a detailed receipt after each session to submit to your insurance provider. Many extended health plans cover services with a Registered Psychotherapist (Qualifying) — please confirm your specific coverage with your provider."],
  ["How do I know if psychotherapy is right for me?", "The complimentary 15-minute consultation exists for exactly this question. There's no commitment—just a conversation about what you're carrying and whether this space feels right."],
  ["What is your cancellation policy?", "We require 48 hours' notice to cancel or change an appointment, or you will be required to pay the full fee for the missed appointment."],
  ["Do you work with clients of South Asian or Muslim backgrounds?", "Yes—this is a particular focus of the practice. Haya Healing Psychotherapy works with South Asian and Muslim individuals, couples, and families navigating cultural expectations, in-law dynamics, intergenerational patterns, and identity, with sessions available in English and Urdu."],
];

const INSTAGRAM_URL = "https://www.instagram.com/hayahealingpsychotherapy/";

const IG_POSTS = [
  { img: "https://behold.pictures/eyJ1IjoiaDZLMnBEa2pHRlFUSHBUQUJPWm13dlhWdmc0MiIsImYiOiJZZjdKS0dXdU5YbXdjc1ZlNmxSciIsInAiOiIxODM3NTE2MzE4MDIzMzI1MiIsImgiOiJleGppNmkifQ.jpg?class=squareMedium", url: "https://www.instagram.com/p/DdEeHPiDvN4/", alt: "Healthy communication starts with listening, honesty, and respect" },
  { img: "https://behold.pictures/eyJ1IjoiaDZLMnBEa2pHRlFUSHBUQUJPWm13dlhWdmc0MiIsImYiOiJZZjdKS0dXdU5YbXdjc1ZlNmxSciIsInAiOiIxODAyMzgyMzU1NDkxMTg4NyIsImgiOiJxNW83YzQifQ.jpg?class=squareMedium", url: "https://www.instagram.com/p/DcPWLsXu9xl/", alt: "A culturally responsive space to explore family dynamics and boundaries" },
  { img: "https://behold.pictures/eyJ1IjoiaDZLMnBEa2pHRlFUSHBUQUJPWm13dlhWdmc0MiIsImYiOiJZZjdKS0dXdU5YbXdjc1ZlNmxSciIsInAiOiIxNzk3NDg5ODkxNjAwNTA3OCIsImgiOiJ2OXV6NzAifQ.jpg?class=squareMedium", url: "https://www.instagram.com/p/DcKIFv4Ay8v/", alt: "Sometimes strength looks like letting yourself be supported" },
  { img: "https://behold.pictures/eyJ1IjoiaDZLMnBEa2pHRlFUSHBUQUJPWm13dlhWdmc0MiIsImYiOiJZZjdKS0dXdU5YbXdjc1ZlNmxSciIsInAiOiIxNzg4MDY1OTU1MDQ5MjEwNiIsImgiOiJ6b21lOGYifQ.jpg?class=squareMedium", url: "https://www.instagram.com/p/Dbms1rdAhIt/", alt: "Healing begins when you're honest about what you're feeling" },
];

const InstagramFeed = () => (
  <section data-testid="instagram-section" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10">
    <Reveal><Eyebrow>09 · Follow along</Eyebrow></Reveal>
    <div className="flex flex-wrap items-end justify-between gap-6 mt-4">
      <Reveal i={1}>
        <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
          Psychoeducation, on <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="italic text-[#6e4550] underline underline-offset-4 decoration-[#6e4550]/30 hover:decoration-[#6e4550] transition-all">Instagram.</a>
        </h2>
      </Reveal>
      <Reveal i={2}>
        <a data-testid="instagram-profile-link" href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="pill-btn pill-outline text-sm">
          <Instagram size={15} /> @hayahealingpsychotherapy
        </a>
      </Reveal>
    </div>
    <Reveal i={3}>
      <p className="mt-4 max-w-xl text-[#2c1f20]/75 leading-relaxed">
        Bite-sized affirmations, journal prompts, and grounding practices to carry between sessions.
      </p>
    </Reveal>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14">
      {IG_POSTS.map((p, i) => (
        <Reveal key={p.url} i={i}>
          <a data-testid={`instagram-post-${i + 1}`} href={p.url} target="_blank" rel="noreferrer"
            className="block rounded-md overflow-hidden border border-[#2c1f20]/10 group bg-[#eae2d6]">
            <img src={p.img} alt={p.alt} loading="lazy"
              className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.currentTarget.closest("a").style.display = "none"; }} />
          </a>
        </Reveal>
      ))}
    </div>
  </section>
);

const Faq = () => {
  const [open, setOpen] = useState(6);
  return (
    <section id="faq" data-testid="faq-section" className="px-6 md:px-14 py-16 md:py-20 border-t border-[#2c1f20]/10">
      <div className="max-w-3xl">
      <Reveal><Eyebrow>10 · Frequently asked</Eyebrow></Reveal>
      <Reveal i={1}><h2 className="font-serif-display text-3xl md:text-4xl mt-4">Frequently asked <em className="text-[#6e4550]">questions.</em></h2></Reveal>
      <div className="mt-8">
        {FAQS.map(([q, a], i) => (
          <div key={q} className="border-t border-[#2c1f20]/10 last:border-b">
            <button data-testid={`faq-item-${i + 1}`} onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between py-4 text-left font-serif-display text-lg md:text-xl hover:text-[#6e4550] transition-colors">
              {q}
              <span className={`text-[#8a5a63] transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
            </button>
            {open === i && <p className="pb-6 text-[#2c1f20]/70 leading-relaxed max-w-2xl">{a}</p>}
          </div>
        ))}
      </div>
      <Reveal i={2} className="mt-8">
        <a data-testid="fees-page-link" href="/fees" className="pill-btn pill-outline">Fees &amp; Insurance details <ArrowRight size={15} /></a>
      </Reveal>
      </div>
    </section>
  );
};

const Contact = () => (
  <footer id="contact" data-testid="contact-section" className="px-6 md:px-14 py-28 md:py-40 border-t border-[#2c1f20]/10 bg-[#eae2d6]/60">
    <Reveal><Eyebrow>11 · Have questions?</Eyebrow></Reveal>
    <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl">Book a <em className="text-[#6e4550]">complimentary initial consultation.</em></h2></Reveal>
    <Reveal i={2}>
      <p className="mt-4 max-w-xl text-[#2c1f20]/75 leading-relaxed">
        Reaching out is the first—and often hardest—step. Whatever you're holding, you don't need to carry it alone.
      </p>
    </Reveal>
    <Reveal i={3} className="mt-8 flex flex-wrap gap-6 items-center">
      <a data-testid="footer-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book Initial Consult <ArrowUpRight size={15} /></a>
      <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
      <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
    </Reveal>
    <p className="mt-14 pt-6 border-t border-[#2c1f20]/10 text-xs text-[#2c1f20]/50 max-w-2xl leading-relaxed">
      Psychotherapy is not a substitute for emergency or crisis services. If you are in crisis, call or text 988 (Suicide Crisis Helpline, available 24/7 across Canada) or 911, or go to your nearest emergency department.
    </p>
    <p className="mt-6 text-xs text-[#2c1f20]/40">Haya Healing Psychotherapy · Milton &amp; Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
  </footer>
);

const HomePage = () => {
  useEffect(() => {
    document.title = "Psychotherapist in Milton & Burlington | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Evidence-based couples, family & individual therapy in Milton & Burlington, and virtual across Ontario. South Asian couples therapy in English & Urdu with Momna Sajid, RP (Qualifying). Complimentary 15-minute consultation.");
  }, []);
  return (
  <div className="min-h-screen">
    <SiteHeader />
    <Hero />
    <TrustStrip />
    <AnnouncementMarquee />
    <Meaning />
    <Approach />
    <Steps />
    <TherapistTeaser />
    <Focus />
    <Patterns />
    <Services />
    <Locations />
    <InstagramFeed />
    <Faq />
    <TopBar />
    <Contact />
  </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/clinical" element={<Clinical />} />
        <Route path="/couples" element={<Couples />} />
        <Route path="/cultural" element={<Cultural />} />
        <Route path="/about" element={<About />} />
        <Route path="/concern/:slug" element={<Concern />} />
        <Route path="/approach/:slug" element={<ApproachDetail />} />
        <Route path="/fees" element={<Fees />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/crisis-support" element={<CrisisSupport />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
